import { useState, useEffect, useCallback } from "react";
import { Plus, Edit, Trash2, Clock, ArrowLeft, X } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PageLoader from "../../../helpers/PageLoader";
import SummaryApi from "../../../common/SummaryApi";
import api from "../../../common/apiClient";

const DAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const emptyForm = {
  name: "", startTime: "09:00", endTime: "18:00",
  workingDays: [1, 2, 3, 4, 5, 6],
  graceMinutes: 15, halfDayMinutes: 240, fullDayMinutes: 480,
  breakDurationMinutes: 60, overtimeEligible: false,
  overtimeThreshold: 30, isDefault: false,
};

const ShiftForm = ({ shift, onSave, onClose }) => {
  const [form, setForm] = useState(shift || emptyForm);
  const [saving, setSaving] = useState(false);

  // நேரத்தைக் கணக்கிடும் Function
  const calculateShiftMinutes = useCallback((start, end, breakMins) => {
    if (!start || !end) return { full: 0, half: 0 };

    const [startH, startM] = start.split(":").map(Number);
    const [endH, endM] = end.split(":").map(Number);

    let totalMinutes = (endH * 60 + endM) - (startH * 60 + startM);

    // ஒருவேளை ஷிப்ட் நள்ளிரவைத் தாண்டினால் (e.g. 10 PM to 6 AM)
    if (totalMinutes <= 0) {
      totalMinutes += 24 * 60;
    }

    const netWorkingMinutes = Math.max(0, totalMinutes - (breakMins || 0));
    return {
      full: netWorkingMinutes,
      half: Math.floor(netWorkingMinutes / 2)
    };
  }, []);

  // Start Time, End Time, அல்லது Break மாறும்போது தானாக நிமிடங்கள் Update ஆகும்
  useEffect(() => {
    const { full, half } = calculateShiftMinutes(form.startTime, form.endTime, form.breakDurationMinutes);
    setForm(prev => ({
      ...prev,
      fullDayMinutes: full,
      halfDayMinutes: half
    }));
  }, [form.startTime, form.endTime, form.breakDurationMinutes, calculateShiftMinutes]);

  const toggleDay = (day) => {
    setForm((p) => ({
      ...p,
      workingDays: p.workingDays.includes(day)
        ? p.workingDays.filter((d) => d !== day)
        : [...p.workingDays, day].sort(),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Shift name is required");
    if (form.workingDays.length === 0) return toast.error("Select at least one working day");
    setSaving(true);
    try {
      const res = shift?._id
        ? await api.put(SummaryApi.updateShift.url(shift._id), form)
        : await api.post(SummaryApi.createShift.url, form);
      toast.success(`Shift ${shift ? "updated" : "created"}`);
      onSave(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save shift");
    } finally { setSaving(false); }
  };

  const field = (label, key, type = "text", extra = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input type={type} value={form[key]} {...extra}
        onChange={(e) => setForm((p) => ({ ...p, [key]: type === "number" ? Number(e.target.value) : e.target.value }))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-300"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 top-[57px] md:top-[65px] z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/40 transition-opacity" onClick={onClose} />
      
      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col animate-slide-in">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="font-bold text-gray-800 text-lg">{shift ? "Edit Shift" : "Create Shift"}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form id="shift-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
            {field("Shift Name *", "name")}

            <div className="grid grid-cols-2 gap-4">
              {field("Start Time *", "startTime", "time")}
              {field("End Time *", "endTime", "time")}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Working Days *</label>
              <div className="flex gap-2 flex-wrap">
                {DAYS.map((d) => (
                  <button key={d.value} type="button"
                    onClick={() => toggleDay(d.value)}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                      form.workingDays.includes(d.value)
                        ? "bg-red-600 text-white border-red-600 shadow-md shadow-red-200"
                        : "bg-white text-gray-600 border-gray-300 hover:border-red-400"
                    }`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {field("Grace (mins)", "graceMinutes", "number", { min: 0 })}
              {field("Break (mins)", "breakDurationMinutes", "number", { min: 0 })}
              
              {/* தானாக கணக்கிடப்படுவதால் இதை Read-only அல்லது Highlight செய்யலாம் */}
              <div className="bg-blue-50 p-2 rounded-lg border border-blue-100">
                {field("Half Day (mins)", "halfDayMinutes", "number", { readOnly: true })}
                <p className="text-[10px] text-blue-500 mt-1">* Auto-calculated</p>
              </div>
              <div className="bg-blue-50 p-2 rounded-lg border border-blue-100">
                {field("Full Day (mins)", "fullDayMinutes", "number", { readOnly: true })}
                <p className="text-[10px] text-blue-500 mt-1">* Auto-calculated</p>
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-700">Overtime Eligible</span>
                  {form.overtimeEligible && (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 uppercase tracking-wider">Threshold:</span>
                      <input type="number" value={form.overtimeThreshold} min={0}
                        onChange={(e) => setForm((p) => ({ ...p, overtimeThreshold: Number(e.target.value) }))}
                        className="w-16 border border-gray-300 rounded px-2 py-0.5 text-xs focus:ring-1 focus:ring-red-500"
                      />
                    </div>
                  )}
                </div>
                <button type="button"
                  onClick={() => setForm((p) => ({ ...p, overtimeEligible: !p.overtimeEligible }))}
                  className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${form.overtimeEligible ? "bg-red-600" : "bg-gray-300"}`}>
                  <span className={`inline-block h-4 w-4 mt-1 ml-1 rounded-full bg-white transition-transform ${form.overtimeEligible ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div>
                  <span className="text-sm font-medium text-gray-700">Set as Default</span>
                </div>
                <button type="button"
                  onClick={() => setForm((p) => ({ ...p, isDefault: !p.isDefault }))}
                  className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${form.isDefault ? "bg-red-600" : "bg-gray-300"}`}>
                  <span className={`inline-block h-4 w-4 mt-1 ml-1 rounded-full bg-white transition-transform ${form.isDefault ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
            </div>
          </form>

          <div className="p-6 border-t bg-gray-50 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 bg-white border border-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">
              Cancel
            </button>
            <button type="submit" form="shift-form" disabled={saving}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-red-100">
              {saving ? "Saving…" : (shift ? "Update Shift" : "Create Shift")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ShiftManagement() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formState, setFormState] = useState({ open: false, shift: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(SummaryApi.getAllShifts.url);
      setShifts(res.data.data || []);
    } catch { toast.error("Failed to load shifts"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = (saved) => {
    setShifts((prev) =>
      prev.find((s) => s._id === saved._id)
        ? prev.map((s) => (s._id === saved._id ? saved : s))
        : [saved, ...prev]
    );
    setFormState({ open: false, shift: null });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this shift?")) return;
    try {
      await api.delete(SummaryApi.deleteShift.url(id));
      setShifts((prev) => prev.filter((s) => s._id !== id));
      toast.success("Shift deactivated");
    } catch { toast.error("Failed to delete shift"); }
  };

  const dayNames = (days) => DAYS.filter((d) => days?.includes(d.value)).map((d) => d.label).join(", ");

  return (
    <div className="p-4">
      {/* Header Optimized for Mobile */}
      <div className="mb-6 flex flex-row justify-between items-center gap-2">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-lg bg-red-50 border border-red-100 hover:border-red-500 text-red-600 p-2 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-red-100 rounded-lg hidden xs:block"><Clock className="w-5 h-5 text-red-600" /></div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 whitespace-nowrap tracking-tight">
              Shift<span className="hidden sm:inline"> Management</span>
            </h1>
          </div>
        </div>
        <button onClick={() => setFormState({ open: true, shift: null })}
          className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-pink-600 text-white p-2.5 sm:px-4 sm:py-2 rounded-lg hover:shadow-lg transition-all flex-shrink-0">
          <Plus className="w-5 h-5" /> 
          <span className="hidden sm:block text-sm font-semibold">Add Shift</span>
        </button>
      </div>

      {loading ? <PageLoader /> : shifts.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
          <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 font-medium">No shifts created yet.</p>
          <button onClick={() => setFormState({ open: true, shift: null })} className="mt-4 text-red-600 font-bold hover:underline">Create your first shift</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shifts.map((shift) => (
            <div key={shift._id} className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col gap-4 hover:shadow-md transition-all group relative">
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-gray-900 truncate">{shift.name}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {shift.isDefault && <span className="text-[10px] bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-md uppercase">Default</span>}
                    {shift.overtimeEligible && <span className="text-[10px] bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-md uppercase">OT Enabled</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setFormState({ open: true, shift })}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(shift._id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{shift.startTime} – {shift.endTime}</span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed px-1">
                  <span className="font-semibold text-gray-400 uppercase mr-1">Days:</span>
                  {dayNames(shift.workingDays)}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2 border-t pt-4">
                <div className="text-center">
                  <p className="text-[10px] text-gray-400 uppercase">Grace</p>
                  <p className="text-xs font-bold text-gray-700">{shift.graceMinutes}m</p>
                </div>
                <div className="text-center border-x">
                  <p className="text-[10px] text-gray-400 uppercase">Break</p>
                  <p className="text-xs font-bold text-gray-700">{shift.breakDurationMinutes}m</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-gray-400 uppercase">Half-Day</p>
                  <p className="text-xs font-bold text-gray-700">{shift.halfDayMinutes}m</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {formState.open && (
        <ShiftForm shift={formState.shift} onSave={handleSave} onClose={() => setFormState({ open: false, shift: null })} />
      )}
    </div>
  );
}